<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-primary" :disabled="saving" @click="handleSave">
          <span v-if="saving">儲存中...</span>
          <span v-else>儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">

    <!-- 對話介面 URL（常駐顯示） -->
    <div class="chat-url-card">
      <div class="chat-url-card-label">
        <i class="ri-link"></i> 對話介面 URL
      </div>
      <div class="chat-url-card-body">
        <input
          :value="chatUrl"
          type="text"
          readonly
          class="form-control url-input"
          ref="urlInput"
        />
        <div class="url-actions">
          <button
            type="button"
            class="btn btn-secondary"
            @click="copyUrl"
            :title="copied ? '已複製！' : '複製網址'"
          >
            <i :class="copied ? 'ri-check-line' : 'ri-file-copy-line'"></i>
            {{ copied ? '已複製' : '複製' }}
          </button>
          <a
            :href="chatUrl"
            target="_blank"
            class="btn btn-primary"
            title="在新視窗開啟"
          >
            <i class="ri-external-link-line"></i>
            開啟
          </a>
        </div>
      </div>
      <small class="chat-url-hint">此 URL 可直接嵌入網站或分享給客戶，無需登入即可使用 AI 客服功能。</small>
    </div>

    <!-- 網站嵌入程式碼 -->
    <div class="embed-card">
      <div class="embed-card-header">
        <div class="embed-card-label">
          <i class="ri-code-s-slash-line"></i> 嵌入網站（浮動客服視窗）
        </div>
        <button
          type="button"
          class="btn btn-secondary btn-sm"
          @click="copyEmbed"
        >
          <i :class="copiedEmbed ? 'ri-check-line' : 'ri-file-copy-line'"></i>
          {{ copiedEmbed ? '已複製' : '複製程式碼' }}
        </button>
      </div>
      <p class="embed-card-desc">將以下程式碼貼入網站的 <code>&lt;/body&gt;</code> 標籤之前，即可在網站右下角顯示浮動 AI 客服按鈕。</p>
      <pre class="embed-snippet"><code>{{ embedSnippet }}</code></pre>
      <p class="embed-card-hint">
        <i class="ri-information-line"></i>
        也可以呼叫 <code>ChatmateWidget.open()</code> / <code>ChatmateWidget.close()</code> 以程式方式控制視窗。
      </p>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchSettings">重試</button>
    </div>

    <div v-else class="ai-settings">
      <!-- 頁籤 -->
      <div class="tabs">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          class="tab-btn"
          :class="{ active: activeTab === tab.key }"
          @click="activeTab = tab.key"
        >
          <i :class="tab.icon"></i>
          {{ tab.label }}
        </button>
      </div>

      <form @submit.prevent="handleSave">
        <!-- Tab: AI 設定 -->
        <div v-show="activeTab === 'ai'">
          <div class="form-section">
            <h2>模型設定</h2>

            <div class="form-group">
              <label>AI 模型 <span class="required">*</span></label>
              <select v-model="formData.model_id" class="form-control" required>
                <option value="gpt-4o-mini">GPT-4o Mini（推薦，成本較低）</option>
                <option value="gpt-4o">GPT-4o（高品質）</option>
                <option value="gpt-4-turbo">GPT-4 Turbo（高品質）</option>
              </select>
              <small class="form-hint">選擇使用的 OpenAI 模型</small>
            </div>

            <div class="form-group">
              <label>溫度參數</label>
              <input
                v-model.number="formData.temperature"
                type="number"
                step="0.1"
                min="0"
                max="1"
                class="form-control"
              />
              <small class="form-hint">
                <strong>效果說明：</strong>控制 AI 回應的創意性和一致性。數值越高，回答越有創意和多樣性；數值越低，回答越穩定和可預測。
                <ul>
                  <li><strong>低溫度（0.0-0.3）：</strong>回答更精準、一致，適合需要準確資訊的場景（如客服、資料查詢）</li>
                  <li><strong>中溫度（0.4-0.7，推薦）：</strong>平衡創意和準確性，適合一般對話和問答</li>
                  <li><strong>高溫度（0.8-1.0）：</strong>回答更有創意和多樣性，適合需要創意內容的場景</li>
                </ul>
                <strong>預設值：</strong>0.7
              </small>
            </div>

            <div class="form-group">
              <label>最大 Token 數</label>
              <input
                v-model.number="formData.max_tokens"
                type="number"
                min="1"
                max="4000"
                class="form-control"
              />
              <small class="form-hint">單次回應的最大 Token 數（預設 2000）</small>
            </div>
          </div>

          <div class="form-section">
            <h2>AI 個性化設定</h2>

            <div class="form-group">
              <label>AI 暱稱</label>
              <input
                v-model="formData.ai_nickname"
                type="text"
                class="form-control"
                placeholder="例如：小助手、客服小精靈、AI 助理"
                maxlength="50"
              />
              <small class="form-hint">設定 AI 助理的暱稱，會影響對話中的自我介紹</small>
            </div>

            <div class="form-group">
              <label>AI 個性</label>
              <textarea
                v-model="formData.ai_personality"
                class="form-control"
                rows="3"
                placeholder="請提供具體且詳細的個性描述..."
                maxlength="500"
              ></textarea>
              <small class="form-hint">
                <strong>填寫提示：</strong>請提供具體的範例描述，越詳細越好。
                <ul>
                  <li><strong>範例 1（粗獷風格）：</strong>喜歡講口頭禪「幹」，兇狠的年輕混混，會講黑話，語氣盡可能粗魯</li>
                  <li><strong>範例 2（溫柔風格）：</strong>講話語調溫和的小女孩，善於描述細節，用詞親切可愛</li>
                  <li><strong>範例 3（專業風格）：</strong>專業嚴謹的企業顧問，說話邏輯清晰，用詞正式但不生硬</li>
                  <li><strong>範例 4（活潑風格）：</strong>熱情開朗的年輕店員，喜歡用表情符號和驚嘆號</li>
                </ul>
                <strong>提示：</strong>描述應包含語氣、用詞習慣、口頭禪、情感表達方式等具體細節。
              </small>
            </div>

            <div class="form-group">
              <label>回覆內容型態</label>
              <select v-model="formData.response_style" class="form-control">
                <option value="detailed">詳細（提供完整且詳細的回答）</option>
                <option value="balanced">平衡（適中的長度，包含必要資訊）</option>
                <option value="concise">精簡（簡潔明瞭，重點突出）</option>
              </select>
              <small class="form-hint">控制 AI 回覆的詳細程度和長度</small>
            </div>
          </div>

          <div class="form-section">
            <h2>系統提示詞</h2>

            <div class="form-group">
              <label>提示詞</label>
              <textarea
                v-model="formData.system_prompt"
                class="form-control"
                rows="6"
                placeholder="輸入系統提示詞，定義 AI 助理的角色和行為..."
              ></textarea>
              <small class="form-hint">定義 AI 助理的角色、語氣和行為準則</small>
            </div>
          </div>

          <div class="form-section">
            <h2>歡迎訊息與提示</h2>

            <div class="form-group">
              <label>預設歡迎訊息</label>
              <textarea
                v-model="formData.welcome_message"
                class="form-control"
                rows="4"
                placeholder="例如：您好！我是 AI 客服助理，很高興為您服務。有什麼問題都可以問我喔！"
              ></textarea>
              <small class="form-hint">首次對話時自動發送的歡迎訊息（如果設定，會在用戶首次發送訊息前自動顯示）</small>
            </div>

            <div class="form-group">
              <label>預設提示詞</label>
              <textarea
                v-model="formData.initial_prompt"
                class="form-control"
                rows="4"
                placeholder="例如：請先自我介紹，並詢問用戶需要什麼協助"
              ></textarea>
              <small class="form-hint">
                <strong>效果說明：</strong>首次對話時的引導提示詞，用於引導 AI 在首次回應時的行為。
                <ul>
                  <li><strong>使用時機：</strong>只在用戶發送第一條訊息時生效，後續對話不會使用</li>
                  <li><strong>用途：</strong>可以引導 AI 在首次回應時進行自我介紹、詢問需求、提供選單等</li>
                  <li><strong>範例：</strong>「請先自我介紹，並詢問用戶需要什麼協助」</li>
                </ul>
                <strong>注意：</strong>此提示詞會加入到系統提示詞中，影響 AI 的首次回應內容和風格。
              </small>
            </div>

            <div class="form-group">
              <label>
                <i class="ri-group-line" style="margin-right:4px;"></i>
                LINE 群組加入時的介紹訊息
              </label>
              <textarea
                v-model="formData.group_welcome_message"
                class="form-control"
                rows="3"
                placeholder="例如：大家好！我是 AI 客服助理，有任何問題都可以 @我 來詢問喔！"
              ></textarea>
              <small class="form-hint">
                當此 LINE 官方帳號被加入群組時，自動傳送此訊息向大家介紹自己。
                清空此欄位則不發送任何介紹訊息。
              </small>
            </div>

            <div class="form-group">
              <label>
                <i class="ri-user-add-line" style="margin-right:4px;"></i>
                新成員加入群組時的歡迎訊息
              </label>
              <textarea
                v-model="formData.member_join_message"
                class="form-control"
                rows="3"
                placeholder="例如：歡迎加入！有任何問題都可以 @我 詢問喔！"
              ></textarea>
              <small class="form-hint">
                當有新成員加入 LINE 群組時，自動傳送此歡迎訊息。清空此欄位則不發送。
                <br>支援動態標籤：
                <code>{name}</code> 替換為加入成員的名字、
                <code>{count}</code> 替換為本次加入的人數。
                <br>範例：<em>歡迎 {name} 加入！有問題可以 @我 詢問喔！</em>
              </small>
            </div>
          </div>
        </div>

        <!-- Tab: 知識庫搜尋 -->
        <div v-show="activeTab === 'rag'">
          <div class="form-section">
            <h2>知識庫搜尋設定</h2>
            <p class="section-description">設定 AI 如何從知識庫中搜尋和回答問題</p>

            <div class="form-group">
              <label class="checkbox-label">
                <input v-model="formData.enable_rag" type="checkbox" />
                <span>啟用知識庫搜尋</span>
              </label>
              <small class="form-hint">
                <strong>效果說明：</strong>啟用後，當用戶提問時，AI 會先從您上傳的知識庫檔案中搜尋相關內容，然後根據找到的內容來回答問題。
              </small>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input v-model="formData.include_faq_in_training" type="checkbox" />
                <span>將常見問題（FAQ）納入 AI 訓練</span>
              </label>
              <small class="form-hint">
                啟用後，您可以在「知識庫」頁面手動同步常見問題至知識庫，AI 會依此回答相關問題。關閉後將無法同步 FAQ。
              </small>
            </div>

            <div v-if="formData.enable_rag" class="form-group">
              <label class="checkbox-label">
                <input v-model="formData.strict_mode" type="checkbox" />
                <span>僅回答知識庫內容（嚴格模式）</span>
              </label>
              <small class="form-hint">
                <strong>效果說明：</strong>
                <ul>
                  <li><strong>啟用時：</strong>AI 只能回答知識庫中的內容。如果問題不在範圍內，AI 會禮貌告知並建議聯繫客服。</li>
                  <li><strong>不啟用時：</strong>AI 可回答知識庫內容，也可使用通用知識回答一般問題。</li>
                </ul>
                <strong>適用場景：</strong>適合需要嚴格控制回答範圍的場景（如法律、醫療等）。
                <br>
                另：無論是否開啟嚴格模式，當客人明確要求聯繫真人客服時，系統會標記轉接並可寄送「AI 客服通知 Email」（於系統參數 → 通知設定）。
              </small>
            </div>

            <div v-if="formData.enable_rag" class="form-group">
              <label>搜尋結果數量</label>
              <input
                v-model.number="formData.rag_result_limit"
                type="number"
                min="1"
                max="10"
                class="form-control"
              />
              <small class="form-hint">
                <strong>效果說明：</strong>設定每次搜尋時從知識庫中返回多少筆相關內容給 AI 參考。
                <ul>
                  <li><strong>數量較少（1-3）：</strong>回答更精準，但可能遺漏相關資訊</li>
                  <li><strong>數量適中（4-6，推薦）：</strong>平衡準確性和完整性</li>
                  <li><strong>數量較多（7-10）：</strong>包含更多相關資訊，但可能混入不相關內容</li>
                </ul>
                <strong>預設值：</strong>5 筆
              </small>
            </div>

            <div v-if="formData.enable_rag" class="form-group">
              <label>相似度閾值</label>
              <input
                v-model.number="formData.rag_similarity_threshold"
                type="number"
                step="0.1"
                min="0"
                max="1"
                class="form-control"
              />
              <small class="form-hint">
                <strong>效果說明：</strong>設定搜尋結果與問題的相似度要求。只有相似度達到此值的內容才會被使用。
                <ul>
                  <li><strong>閾值較高（0.8-1.0）：</strong>只使用非常相關的內容，回答更精準</li>
                  <li><strong>閾值適中（0.6-0.8，推薦）：</strong>平衡相關性和覆蓋範圍</li>
                  <li><strong>閾值較低（0.0-0.6）：</strong>使用更多內容，但可能包含不太相關的資訊</li>
                </ul>
                <strong>預設值：</strong>0.7
              </small>
            </div>
          </div>
        </div>
      </form>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { chatmateService } from '@/api/modules/chatmate.js';
import { showError, showSuccess } from '@/utils/swal.js';
import authService from '@/helpers/authservice/authService.js';

export default {
  name: 'ChatMateSettings',
  setup() {
    const { pageTitle } = usePageTitle('AI 設定');
    const loading = ref(false);
    const saving = ref(false);
    const error = ref(null);
    const copied = ref(false);
    const copiedEmbed = ref(false);
    const urlInput = ref(null);
    const activeTab = ref('ai');

    const tabs = [
      { key: 'ai', label: 'AI 設定', icon: 'ri-robot-line' },
      { key: 'rag', label: '知識庫搜尋', icon: 'ri-book-2-line' }
    ];

    const defaultSystemPrompt = '你是一個友善且專業的客服助理，請用繁體中文回答用戶的問題。';
    const defaultWelcomeMessage = '您好！我是 AI 客服助理，很高興為您服務。有什麼問題都可以問我喔！';

    const formData = reactive({
      model_id: 'gpt-4o-mini',
      temperature: 0.7,
      max_tokens: 2000,
      system_prompt: defaultSystemPrompt,
      enable_rag: true,
      rag_result_limit: 5,
      rag_similarity_threshold: 0.7,
      strict_mode: true,
      ai_nickname: '',
      ai_personality: '',
      response_style: 'balanced',
      welcome_message: defaultWelcomeMessage,
      initial_prompt: '',
      include_faq_in_training: true,
      group_welcome_message: '大家好！我是 AI 客服助理，有任何問題都可以 @我 來詢問喔！',
      member_join_message: ''
    });

    const getChatBaseUrl = () => {
      if (process.env.VUE_APP_CHAT_BASE_URL) return process.env.VUE_APP_CHAT_BASE_URL;
      if (process.env.NODE_ENV === 'development') return 'http://localhost:8081';
      const origin = window.location.origin;
      if (origin.includes('admin.lockline.io')) return origin.replace('admin.lockline.io', 'app.lockline.io');
      if (origin.includes('localhost') || origin.match(/:\d+$/)) return origin.replace(/:\d+$/, ':8081');
      return 'https://app.lockline.io';
    };

    const chatUrl = computed(() => {
      const user = authService.getCurrentUser();
      const clientId = user?.clientId;
      if (!clientId) return '請先登入客戶帳號';
      return `${getChatBaseUrl()}/chat/${clientId}`;
    });

    const copyUrl = async () => {
      try {
        await navigator.clipboard.writeText(chatUrl.value);
        copied.value = true;
        setTimeout(() => { copied.value = false; }, 2000);
      } catch (err) {
        showError('複製失敗', '請手動複製網址');
      }
    };

    const widgetScriptUrl = computed(() => {
      const base = getChatBaseUrl();
      return `${base}/chat/widget.js`;
    });

    const embedSnippet = computed(() => {
      const user = authService.getCurrentUser();
      const clientId = user?.clientId || 'YOUR_CLIENT_ID';
      return `<script>\n  window.ChatmateConfig = {\n    clientId: '${clientId}',\n    buttonLabel: '客服',\n    buttonColor: '#4f46e5',\n    iframeWidth: 380,\n    iframeHeight: 620,\n  };\n<\/script>\n<script src="${widgetScriptUrl.value}" async><\/script>`;
    });

    const copyEmbed = async () => {
      try {
        await navigator.clipboard.writeText(embedSnippet.value);
        copiedEmbed.value = true;
        setTimeout(() => { copiedEmbed.value = false; }, 2000);
      } catch (err) {
        showError('複製失敗', '請手動複製程式碼');
      }
    };

    const fetchSettings = async () => {
      loading.value = true;
      error.value = null;
      try {
        const response = await chatmateService.getAISettings();
        if (response && response.success && response.data) {
          Object.assign(formData, {
            model_id: response.data.model_id || 'gpt-4o-mini',
            temperature: response.data.temperature || 0.7,
            max_tokens: response.data.max_tokens || 2000,
            system_prompt: response.data.system_prompt || defaultSystemPrompt,
            enable_rag: response.data.enable_rag !== false,
            rag_result_limit: response.data.rag_result_limit || 5,
            rag_similarity_threshold: response.data.rag_similarity_threshold || 0.7,
            strict_mode: response.data.strict_mode !== false,
            ai_nickname: response.data.ai_nickname || '',
            ai_personality: response.data.ai_personality || '',
            response_style: response.data.response_style || 'balanced',
            welcome_message: response.data.welcome_message || defaultWelcomeMessage,
            initial_prompt: response.data.initial_prompt || '',
            include_faq_in_training: response.data.include_faq_in_training !== false,
            group_welcome_message: response.data.group_welcome_message || '大家好！我是 AI 客服助理，有任何問題都可以 @我 來詢問喔！',
            member_join_message: response.data.member_join_message || ''
          });
        } else {
          error.value = response?.message || '獲取 AI 設定失敗';
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '獲取 AI 設定失敗';
        await showError('載入失敗', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleSave = async () => {
      saving.value = true;
      try {
        const response = await chatmateService.updateAISettings(formData);
        if (response && response.success) {
          await showSuccess('儲存成功', 'AI 設定已更新');
          await fetchSettings();
        } else {
          await showError('儲存失敗', response?.message || '更新 AI 設定失敗');
        }
      } catch (err) {
        await showError('儲存失敗', err.response?.data?.message || err.message || '更新 AI 設定失敗');
      } finally {
        saving.value = false;
      }
    };

    onMounted(() => { fetchSettings(); });

    return {
      pageTitle, loading, saving, error, formData,
      fetchSettings, handleSave,
      chatUrl, copied, urlInput, copyUrl,
      embedSnippet, copiedEmbed, copyEmbed,
      activeTab, tabs
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

/* 對話介面 URL 常駐卡片 */
.chat-url-card {
  background: white;
  border: 1px solid #d0e8ff;
  border-left: 4px solid #007bff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.06);
}

.chat-url-card-label {
  font-size: 13px;
  font-weight: 600;
  color: #007bff;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.chat-url-card-body {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.chat-url-hint {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #888;
}

.url-input {
  flex: 1;
  font-family: 'Courier New', monospace;
  background: #f8f9fa;
  font-size: 13px;
}

.url-actions {
  display: flex;
  gap: 8px;
}

.url-actions .btn {
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 嵌入程式碼卡片 */
.embed-card {
  background: white;
  border: 1px solid #d4edda;
  border-left: 4px solid #28a745;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(40, 167, 69, 0.06);
}

.embed-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.embed-card-label {
  font-size: 13px;
  font-weight: 600;
  color: #28a745;
  display: flex;
  align-items: center;
  gap: 6px;
}

.embed-card-desc {
  font-size: 13px;
  color: #555;
  margin: 0 0 10px;
  line-height: 1.6;
}

.embed-snippet {
  background: #1e1e2e;
  border-radius: 8px;
  padding: 14px 16px;
  margin: 0 0 12px;
  overflow-x: auto;
  font-family: 'Courier New', monospace;
  font-size: 12.5px;
  line-height: 1.7;
  color: #cdd6f4;
  white-space: pre;
}

.embed-snippet code {
  font-family: inherit;
  color: inherit;
}


.embed-card-hint {
  font-size: 12px;
  color: #888;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.embed-card-hint code {
  background: #f0f4f8;
  border-radius: 3px;
  padding: 1px 5px;
  font-size: 11.5px;
  color: #444;
}

.embed-card .btn-sm {
  padding: 4px 10px;
  font-size: 12px;
}

/* 頁籤 */
.tabs {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 24px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  color: #888;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  color: #007bff;
}

.tab-btn.active {
  color: #007bff;
  border-bottom-color: #007bff;
  font-weight: 600;
}

/* 設定表單 */
.ai-settings {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-section {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e0e0e0;
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section h2 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #333;
}

.required {
  color: #e74c3c;
}

.form-hint {
  display: block;
  margin-top: 4px;
  font-size: 13px;
  color: #666;
  line-height: 1.6;
}

.form-hint strong {
  color: #333;
  font-weight: 600;
}

.form-hint ul {
  margin: 6px 0 0 20px;
  padding: 0;
  list-style-type: disc;
}

.form-hint ul li {
  margin: 4px 0;
  line-height: 1.5;
}

.section-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  cursor: pointer;
}

textarea.form-control {
  resize: vertical;
  min-height: 120px;
}

.form-actions {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}
</style>
